import { useState } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dag3tthtto0ceqqnhleg";

type Status = "idle" | "sending" | "success" | "error";

function Newsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    data.delete("website_alt");

    if (status === "sending") return;

    setStatus("sending");
    setFormError("");

    // anti-spam: silently show success-like feedback if a bot filled the honeypot
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    try {
      const payload = new URLSearchParams();
      data.forEach((value, key) => {
        const v = String(value).trim();
        if (v) payload.append(key, v);
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(meta?.message ?? parsed?.message ?? meta?.detail ?? "");

      const ok =
        response.ok &&
        parsed?.code === "OK" &&
        !/spam/i.test(responseText) &&
        !/spam/i.test(serverMsg);

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试，或改用邮件联系我们。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  }

  return (
    <section id="subscribe" className="scroll-mt-24 bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 rounded-2xl border border-background-200 bg-background-50 px-6 py-8 md:px-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-700">
              <i className="ri-mail-send-line" /> 快讯订阅
            </span>
            <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              每天一次，把当天的瓜打包发给你
            </h2>
            <p className="mt-3 max-w-lg text-sm text-foreground-600 md:text-base">
              订阅瓜场快讯，每日精选值得关注的热点与观点合集。不刷屏、不发广告，错过的热门随时补课。
            </p>
          </div>

          <form
            id="melon-subscribe-form"
            data-readdy-form
            onSubmit={handleSubmit}
            noValidate
            className="flex flex-col gap-3"
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                name="email"
                required
                placeholder="你的邮箱地址"
                aria-label="邮箱地址"
                className="h-12 flex-1 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {status === "sending" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 提交中
                  </>
                ) : (
                  <>
                    立即订阅 <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>
            </div>

            {/* anti-spam honeypot */}
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="field-aux"
            />

            {status === "success" && (
              <p className="flex items-center gap-1.5 text-sm font-medium text-secondary-700">
                <i className="ri-checkbox-circle-line" /> 订阅成功！最新瓜讯将很快发到你的邮箱，记得查收。
              </p>
            )}
            {status === "error" && (
              <p className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
                <i className="ri-error-warning-line" /> {formError}
              </p>
            )}
            <p className="text-xs text-foreground-400">
              提交即表示你同意接收瓜场快讯邮件，随时可一键退订。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Newsletter;